// The AI cell: one sparkles icon when any AI capability is on, naming each one, or a muted dash. Two
// capabilities come with the row (its course options); AI-graded questions come from the AI service through
// AiFeaturesContext, a map by coursenum the page fills after the rows load. A course missing from the map
// is pending, null means the service could not answer, and the cell updates in place when it does.

import React, { createContext, useContext } from 'react'
import PropTypes from 'prop-types'

import { Tooltip } from '../../components/me_table/tooltip/Tooltip'
import { Dash } from './InstructorBadges'

/** {coursenum: true|false|null}; a course with no entry is still pending. */
export const AiFeaturesContext = createContext({})

export const AI_NAMES = {
  chatbot: 'Chatbot (student AI assistant)',
  clarify: 'AI answer clarification',
  grading: 'AI-graded questions',
}
export const AI_UNAVAILABLE = 'AI-graded questions status unavailable'
export const AI_PENDING = 'Checking AI features'
export const AI_NONE = 'No AI features enabled'

/**
 * What one course's AI cell shows.
 * @param {{chatbot?: boolean, clarify?: boolean}} options the course's AI options
 * @param {boolean|null|undefined} grading the AI service's answer: undefined while pending, null unavailable
 * @returns {{icon: boolean, label: string}}
 */
export const aiState = (options, grading) => {
  const ai = options || {}
  const names = []
  if (ai.chatbot) names.push(AI_NAMES.chatbot)
  if (ai.clarify) names.push(AI_NAMES.clarify)
  if (grading === true) names.push(AI_NAMES.grading)
  if (names.length) {
    const label = `AI enabled: ${names.join(', ')}`
    return {
      icon: true,
      label: grading === null ? `${label}, ${AI_UNAVAILABLE}` : label,
    }
  }
  if (grading === undefined) return { icon: false, label: AI_PENDING }
  if (grading === null) return { icon: false, label: AI_UNAVAILABLE }
  return { icon: false, label: AI_NONE }
}

export const AiCell = ({ row }) => {
  const features = useContext(AiFeaturesContext) || {}
  const { icon, label } = aiState(row.ai, features[row.coursenum])
  if (!icon) return <Dash label={label} />
  return (
    <Tooltip text={label}>
      <span
        className="admin-courses-ai"
        role="img"
        tabIndex={0}
        aria-label={label}
      >
        <i className="fa-regular fa-sparkles" aria-hidden="true" />
      </span>
    </Tooltip>
  )
}

AiCell.propTypes = {
  row: PropTypes.shape({
    coursenum: PropTypes.string,
    ai: PropTypes.shape({ chatbot: PropTypes.bool, clarify: PropTypes.bool }),
  }).isRequired,
}
